import {
  describeDeterministicQueryError,
  type DeterministicQueryErrorKind,
} from "#describe-deterministic-query-error";
import {
  describeTransientNetworkError,
  type TransientNetworkErrorKind,
} from "#is-transient-network-error";

/**
 * Every failure category the app distinguishes when deciding what to tell a user, in one place.
 *
 * `"unknown"` is the honest default: an error nobody has classified is still a real failure that
 * deserves a real screen, it just gets the generic copy. It is NOT a bucket for "probably
 * transient" — see `isTransientNetworkError` for why over-suppression is worse than a visible error.
 */
export type UserFacingFailureKind =
  | TransientNetworkErrorKind
  | DeterministicQueryErrorKind
  | "study-data-unavailable"
  | "unknown";

/** Names the actor database layer gives a failed open (checked by name: no dependency upward). */
const STUDY_DATA_UNAVAILABLE_ERROR_NAMES = new Set([
  "ActorDatabaseUnavailableError",
  "DatabaseRecoveryRequiredError",
]);

/**
 * An enforced deadline fired (`DeadlineExceededError`, checked by name: no dependency upward).
 * Most waits it bounds are local — a database lock, a native call, a layout that never reported —
 * so "No connection" would send the user to fix a network that is fine.
 */
function isDeadlineExceededError(error: unknown): boolean {
  let current = error;
  for (let depth = 0; typeof current === "object" && current !== null && depth < 10; depth += 1) {
    if (Reflect.get(current, "name") === "DeadlineExceededError") {
      return true;
    }
    current = Reflect.get(current, "cause");
  }
  return false;
}

/**
 * The on-device study database could not be opened. Everything that needs study data fails the
 * same way until it is recovered; everything else (browsing, search, reference data) still works.
 */
export function isStudyDataUnavailableError(error: unknown): boolean {
  let current = error;
  for (let depth = 0; typeof current === "object" && current !== null && depth < 10; depth += 1) {
    if (STUDY_DATA_UNAVAILABLE_ERROR_NAMES.has(String(Reflect.get(current, "name")))) {
      return true;
    }
    current = Reflect.get(current, "cause");
  }
  return false;
}

export type UserFacingFailure = {
  /** A short sentence about what to do next. Never raw error text. */
  body: string;
  kind: UserFacingFailureKind;
  /** Whether re-issuing the same request unchanged could plausibly succeed. */
  retryable: boolean;
  /** A headline the user can act on. */
  title: string;
};

/**
 * The user-facing description of a failure: what to call it, what to say, and whether a retry is
 * worth offering as the primary action.
 *
 * Shared by the page-level error screen and the navigation-failure toast so the two never drift —
 * a user who sees "Haniverse needs an update" on one screen and "Something went wrong" for the very same
 * failure on the next would rightly conclude the app does not know what is wrong with it.
 *
 * Classification order matters and is deliberate: a transient failure wins when both match, because
 * a canceled request whose message happens to quote a SQL string is still a canceled request.
 */
export function describeFailureForUser(error: unknown): UserFacingFailure {
  const transientKind = describeTransientNetworkError(error);
  if (transientKind !== null && isDeadlineExceededError(error)) {
    return {
      body: "Something didn't respond in time. Try again, and if it keeps happening, restart Haniverse.",
      kind: transientKind,
      retryable: true,
      title: "This is taking too long",
    };
  }
  if (transientKind !== null) {
    return {
      body: "Check your connection and try again.",
      kind: transientKind,
      retryable: true,
      title: "No connection",
    };
  }

  if (isStudyDataUnavailableError(error)) {
    return {
      // Rare, and not something the user can fix alone: recovery may need a reset or a sign-out,
      // which support walks them through. Retrying cannot help while the open stays failed.
      body: "Haniverse couldn't open your study data on this device. Please contact support so we can help.",
      kind: "study-data-unavailable",
      retryable: false,
      title: "Something went wrong with your study data",
    };
  }

  const deterministicKind = describeDeterministicQueryError(error);
  if (deterministicKind === "schema-mismatch") {
    return {
      // The remedy really is an update: the app is querying data the server no longer has (or,
      // offline, a bundled file that has fallen behind the app). Retrying the same query cannot
      // succeed, so "try again" is offered only as a fallback, not as the fix.
      body: "This version of Haniverse is out of date for its reference data. Update Haniverse, then try again.",
      kind: deterministicKind,
      retryable: false,
      title: "Haniverse needs an update",
    };
  }
  if (deterministicKind === "invalid-query") {
    return {
      // Our bug, not the user's. There is nothing they can change, so the copy stays generic —
      // but it is still deterministic, so it must not be silently retried either.
      body: "This page couldn't load. You can try again, or head back home.",
      kind: deterministicKind,
      retryable: false,
      title: "Something went wrong",
    };
  }

  return {
    body: "This page couldn't load. You can try again, or head back home.",
    kind: "unknown",
    retryable: true,
    title: "Something went wrong",
  };
}
